import React, { useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDashboardCounters, type DashboardCounters } from '../../hooks/useDashboardCounters';
import { AccountDrawer } from '../../components/portal/AccountDrawer';
import { visibleAccountNav } from '../../components/portal/accountNav';
import { SoloCliente } from '../../components/portal/SoloCliente';
import { useContatto } from '../../hooks/useContatto';

export default function CustomerLayout() {
  return <CustomerLayoutInner />;
}

function CustomerLayoutInner() {
  const { user, logout } = useAuth();
  const contatto = useContatto();
  // Il Contatto non ha un Cliente: l'endpoint dei contatori gli risponde 404.
  const { data: counters, reload: ricaricaContatori } = useDashboardCounters(
    (user?.ruolo === 'cliente' || user?.ruolo === 'cliente_subordinato') && !contatto,
  );
  // I chip seguono ciò che l'utente vede: a ogni cambio di pagina si
  // ricontano (un ordine aperto, un messaggio letto).
  const { pathname } = useLocation();
  useEffect(() => { void ricaricaContatori(); }, [pathname, ricaricaContatori]);
  const groups = visibleAccountNav(user?.ruolo, contatto);

  const onLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-[hsl(0,0%,98%)]" data-testid="customer-layout">
      {/* Navigazione mobile: barra di sezione + drawer. Sta **fuori** dal
          contenitore della griglia, che ha `px-4 py-6`: dentro, il `py-6`
          le metteva 25px di stacco sopra e i `px-4` la rientravano, così
          non risultava attaccata alla navbar. Qui è a piena larghezza e
          sticky subito sotto l'header. */}
      <AccountDrawer counters={counters} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar desktop (RF-GEN-01) — invariata da lg in su, nascosta
            sotto perché la sua funzione passa al drawer. */}
        <aside
          className="no-print hidden lg:block lg:sticky lg:top-6 lg:self-start"
          data-testid="customer-sidebar"
        >
          <div className="rounded-xl border bg-white overflow-hidden">
            {/* Card utente */}
            <div className="px-4 py-3 border-b bg-[hsl(210,40%,97%)]">
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Account</div>
              <div className="font-semibold text-sm truncate" title={user?.nome ?? ''}>
                {user?.nome ?? user?.email ?? '—'}
              </div>
              {user?.idCliente && (
                <div className="text-[11px] font-mono text-muted-foreground">cod. {user.idCliente}</div>
              )}
            </div>

            {/* Nav items — stesso modello raggruppato del drawer, così le
                due navigazioni non possono divergere. */}
            <nav className="py-2" aria-label="Navigazione area clienti">
              {groups.map((group, gi) => (
                <div key={group.label ?? `g${gi}`}>
                  {group.label && (
                    <div className="px-4 pb-1 pt-3 text-[11px] font-semibold text-muted-foreground">
                      {group.label}
                    </div>
                  )}
                  {group.items.map(item => {
                    const Icon = item.icon;
                    const count = item.badge ? counters[item.badge as keyof DashboardCounters] : 0;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.exact}
                        data-testid={item.testId}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                            isActive
                              ? 'bg-[hsl(210,64%,29%)] text-white font-semibold'
                              : 'text-[#3a3a36] hover:bg-[hsl(210,40%,96%)]'
                          }`
                        }
                      >
                        <Icon className="w-4 h-4 flex-none" />
                        <span className="truncate flex-1">{item.label}</span>
                        {count > 0 && (
                          <span
                            data-testid={`${item.testId}-badge`}
                            className="ml-auto inline-flex items-center justify-center text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white"
                          >
                            {count > 99 ? '99+' : count}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              ))}
              <button
                onClick={onLogout}
                data-testid="nav-logout-desktop"
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-rose-700 hover:bg-rose-50 mt-2 pt-2 border-t"
              >
                <LogOut className="w-4 h-4 flex-none" />
                <span>Log out</span>
              </button>
            </nav>
          </div>
        </aside>

        {/* Contenuto */}
        <main className="min-w-0">
          {/* Le pagine che chiedono un Cliente, per un Contatto: un messaggio. */}
          <SoloCliente>
            <Outlet />
          </SoloCliente>
        </main>
      </div>
    </div>
  );
}
